/**
 * The first reply to a shopper request, prefilled — and the WhatsApp, email
 * and phone links that carry it.
 *
 * Written in the SHOPPER's language (the request's `lang`), not the vendor's:
 * the vendor reads it once before sending, the shopper reads it for real. One
 * template per kind, a full sentence per case (with or without a product, a
 * date, a store) rather than fragments glued together — fragments do not
 * survive translation, Arabic least of all.
 *
 * Pure: the caller passes a `t` fixed to the shopper's language
 * (`i18n.getFixedT(lang)`).
 */
import { Translate, areaFigure, formatDay, productLine } from './request-format';
import type { RequestView } from './types';

export type { Translate };

export const REPLY_LANGUAGES = ['en', 'fr', 'es', 'ar'] as const;
export type ReplyLanguage = (typeof REPLY_LANGUAGES)[number];

const P = 'SHOPPER_REQUESTS.REPLY';

/** The shopper's language when we know it and have it, else the vendor's, else English. */
export function replyLanguage(requestLang: string | null | undefined, vendorLang?: string | null): ReplyLanguage {
    for (const candidate of [requestLang, vendorLang]) {
        const l = (candidate || '').slice(0, 2).toLowerCase();
        if ((REPLY_LANGUAGES as readonly string[]).includes(l)) return l as ReplyLanguage;
    }
    return 'en';
}

/** A first name to greet with: the first word of the name, never an email. */
export function greetingName(name: string | null | undefined): string {
    const first = (name || '').trim().split(/\s+/)[0] || '';
    return first.includes('@') ? '' : first;
}

export interface ReplyMessage {
    subject: string;
    body: string;
}

type Request = Pick<RequestView, 'kind' | 'contact' | 'items' | 'store' | 'preferredDate' | 'preferredSlot' | 'areaM2' | 'areaRange'>;

/** Subject and body for the first reply, in `lang`. */
export function buildReplyMessage(request: Request, t: Translate, opts: { lang: ReplyLanguage; vendorName?: string | null }): ReplyMessage {
    const { lang } = opts;
    const line = productLine(request.items);
    const product = line ? (line.more > 0 ? t(`${P}.PRODUCT_AND_MORE`, { product: line.title, count: line.more }) : line.title) : '';
    const store = request.store?.name?.trim() || '';
    const name = greetingName(request.contact?.name);

    const greeting = name ? t(`${P}.GREETING`, { name }) : t(`${P}.GREETING_NO_NAME`);
    const lines: string[] = [];

    switch (request.kind) {
        case 'sample':
            lines.push(product ? t(`${P}.SAMPLE`, { product }) : t(`${P}.SAMPLE_GENERIC`));
            lines.push(store ? t(`${P}.SAMPLE_AT_STORE`, { store }) : t(`${P}.SAMPLE_ADDRESS`));
            break;
        case 'visit': {
            lines.push(product ? t(`${P}.VISIT`, { product }) : t(`${P}.VISIT_GENERIC`));
            const day = formatDay(request.preferredDate, lang, 'long');
            const when = day
                ? request.preferredSlot
                    ? t(`${P}.WHEN_SLOT`, { date: day, slot: t(`${P}.SLOT.${request.preferredSlot.toUpperCase()}`) })
                    : day
                : '';
            if (when && store) lines.push(t(`${P}.VISIT_AT_STORE_ON`, { store, when }));
            else if (when) lines.push(t(`${P}.VISIT_ON`, { when }));
            else if (store) lines.push(t(`${P}.VISIT_AT_STORE_ASK_DAY`, { store }));
            else lines.push(t(`${P}.VISIT_ASK_DAY`));
            break;
        }
        case 'quote': {
            const area = areaFigure(request.areaM2, request.areaRange, lang);
            if (product && area) lines.push(t(`${P}.QUOTE_AREA`, { product, area: area.text }));
            else if (product) lines.push(t(`${P}.QUOTE`, { product }));
            else lines.push(t(`${P}.QUOTE_GENERIC`));
            break;
        }
        case 'callback':
            lines.push(product ? t(`${P}.CALLBACK`, { product }) : t(`${P}.CALLBACK_GENERIC`));
            break;
        case 'buy':
        default:
            lines.push(product ? t(`${P}.BUY`, { product }) : t(`${P}.BUY_GENERIC`));
            break;
    }

    const vendor = (opts.vendorName || '').trim();
    const signoff = vendor ? t(`${P}.SIGNOFF`, { vendor }) : t(`${P}.SIGNOFF_NO_VENDOR`);
    const kindKey = (request.kind || 'buy').toUpperCase();
    const subject = product ? t(`${P}.SUBJECT.${kindKey}`, { product }) : t(`${P}.SUBJECT.${kindKey}_GENERIC`);

    return { subject, body: [greeting, '', lines.join(' '), '', signoff].join('\n') };
}

// ── Links ───────────────────────────────────────────────────────────────

/** wa.me chat with the text typed in; '' when there is no usable number. */
export function whatsappLink(digits: string | null | undefined, text: string): string {
    const number = String(digits || '').replace(/\D/g, '');
    if (number.length < 6) return '';
    return `https://wa.me/${number}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}

/** mailto: with subject and body prefilled (CRLF line breaks, as RFC 6068 asks). */
export function mailtoLink(email: string | null | undefined, subject: string, body: string): string {
    const to = String(email || '').trim();
    if (!to) return '';
    const params = [
        subject ? `subject=${encodeURIComponent(subject)}` : '',
        body ? `body=${encodeURIComponent(body.replace(/\r?\n/g, '\r\n'))}` : '',
    ].filter(Boolean);
    return `mailto:${encodeURIComponent(to).replace(/%40/g, '@')}${params.length ? `?${params.join('&')}` : ''}`;
}

/** tel: with the number's digits and a leading +, if any. */
export function telLink(phone: string | null | undefined): string {
    const raw = String(phone || '').trim();
    const digits = raw.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
    return digits.replace(/\D/g, '').length >= 3 ? `tel:${digits}` : '';
}
